import type { Cart, DepositInfo } from '../types';

/**
 * The figures and wording behind the Payment Element step's order summary
 * (P5.3) — the same rules the Card Element step (Payment.tsx) applies
 * inline, copied rather than shared so no other tenant's payment step is
 * touched (H22). Display only: every amount comes from the server's quote
 * (D68).
 */

export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

export interface DepositSummaryFacts {
  /** The EFFECTIVE subtotal — catalogue price less any promo (D69). */
  subtotalCents: number;
  discountCents: number;
  balanceCents: number;
  depositPct: number | null;
  /**
   * Be You-only: the deposit covers the whole subtotal (a package, a
   * subscription, a sub-$50 service, or the customer chose pay in full),
   * so the copy says "Pay in full" and the balance row goes. Gated to Be
   * You because changing this copy for every tenant risks wording other
   * operators never promised (H22).
   */
  payingInFull: boolean;
  sessionCount: number | null;
  perSessionCents: number | null;
  /** Sales tax inside today's charge (Be You); 0 when none. */
  taxCents: number;
  /** The tax rate as a display percentage ("8.875"), or null. */
  taxPct: string | null;
  /** The balance still owes tax (a deposit on a taxed tenant). */
  balanceIncludesTax: boolean;
}

export function summarizeDeposit(
  cart: Cart,
  deposit: DepositInfo,
  isBeYou: boolean,
): DepositSummaryFacts {
  // The server's deposit is computed on the discounted basis, so summing
  // raw prices would show a total that disagrees with the charge.
  const subtotalCents = cart.lines.reduce(
    (sum, l) => sum + Math.max(0, l.price.amountCents - (l.discountCents ?? 0)),
    0,
  );
  const discountCents = cart.lines.reduce((sum, l) => sum + (l.discountCents ?? 0), 0);
  // Sales tax, from the server's quote (absent for untaxed tenants, so
  // both are 0 and every figure below is what it always was).
  const taxCents = deposit.taxCents ?? 0;
  const fullTaxCents = deposit.fullTaxCents ?? 0;
  const preTaxChargeCents = deposit.amountCents - taxCents;
  const balanceCents = Math.max(0, subtotalCents + fullTaxCents - deposit.amountCents);
  const depositPct =
    subtotalCents > 0 ? Math.round((preTaxChargeCents / subtotalCents) * 1000) / 10 : null;
  const payingInFull = isBeYou && preTaxChargeCents === subtotalCents && subtotalCents > 0;
  const sessionsMatch =
    payingInFull && cart.lines[0] ? cart.lines[0].serviceName.match(/(\d+)\s+sessions?\b/i) : null;
  const sessionCount = sessionsMatch ? parseInt(sessionsMatch[1]!, 10) : null;
  // The PACKAGE line's own price per session — not the whole cart's, which
  // would fold any add-on into the per-session figure. (The Card Element
  // step still divides the cart subtotal; left as is there, H22.)
  const packageLine = cart.lines[0];
  const packageCents = packageLine
    ? Math.max(0, packageLine.price.amountCents - (packageLine.discountCents ?? 0))
    : 0;
  const perSessionCents =
    sessionCount && sessionCount > 1 && packageCents > 0
      ? Math.round(packageCents / sessionCount)
      : null;
  return {
    subtotalCents,
    discountCents,
    balanceCents,
    depositPct,
    payingInFull,
    sessionCount,
    perSessionCents,
    taxCents,
    taxPct:
      deposit.taxRatePpm && deposit.taxRatePpm > 0
        ? String(Number((deposit.taxRatePpm / 10_000).toFixed(4)))
        : null,
    balanceIncludesTax: fullTaxCents > 0 && taxCents === 0,
  };
}

/** The step's title and sub-line for a deposit or pay-in-full payment. */
export function depositCopy(
  deposit: DepositInfo,
  facts: DepositSummaryFacts,
): { title: string; sub: string } {
  const { payingInFull, sessionCount } = facts;
  return {
    title: payingInFull
      ? sessionCount && sessionCount > 1
        ? 'Pay for your package.'
        : 'Pay in full.'
      : 'Hold with a deposit.',
    sub: payingInFull
      ? sessionCount && sessionCount > 1
        ? `${formatMoney(deposit.amountCents, deposit.currency)} prepays all ${sessionCount} sessions at package pricing — no balance due at your appointments.`
        : `${formatMoney(deposit.amountCents, deposit.currency)} paid in full today — no balance due at your appointment.`
      : (deposit.policyText ??
        `${formatMoney(deposit.amountCents, deposit.currency)} holds your spot. Balance is paid at the appointment.`),
  };
}
